import React, { useEffect } from "react";
import * as R from "ramda";
import { useIntl } from "react-intl";
import { Flex, FormControl, FormLabel, FormErrorMessage, useStyleConfig } from "@chakra-ui/react";

import { RebillyCardResponsiveHandler } from "./styles";

export const validateRebillyField = (state) => {
  return R.path(["lastChange", "error", "message"])(state);
};

export const getErrorCodeFromState = (state) => {
  return R.path(["lastChange", "error", "code"])(state);
};

export const TextField = ({ name, lib, onEvent, label, hasFocus, isTouched, error }) => {
  const id = `rebilly-field-${name}`;
  const isInvalid = isTouched && error;
  const inputStyles = useStyleConfig("Input");

  useEffect(() => {
    try {
      const cardPart = lib.card.mount(`#${id}`, name);
      cardPart.on("change", (data) => onEvent("change", { ...data, name }));
      cardPart.on("focus", () => onEvent("focus", { name }));
      cardPart.on("blur", () => onEvent("blur", { name }));
    } catch (error) {
      // Ignore the "already mounted" error, since it's safe to ignore
      if (error.code !== "already-mounted") {
        throw error;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // We only want to mount the field on initial mount

  return (
    <FormControl isInvalid={isInvalid} data-testid={id}>
      <FormLabel htmlFor={id} required>
        {label}
      </FormLabel>
      <Flex
        sx={{ ...inputStyles.field, padding: 0 }}
        id={id}
        className="rebilly-mount-point"
        data-focus={hasFocus ? true : undefined}
        data-invalid={isInvalid ? true : undefined}
        // NOTE: Leaving this in here for when this component gets isDisabled implemented. No need for now.
        // data-disabled={isDisabled ? true : undefined}
      />

      <FormErrorMessage className="single-field-error-message">{error}</FormErrorMessage>
    </FormControl>
  );
};

export default function RebillyCardTextField({ formState = {}, errors = {}, ...rest }) {
  const { formatMessage } = useIntl();
  const inputFields = {
    cardNumber: formatMessage({
      defaultMessage: "Card Details",
      description: "field label",
    }),
    cardExpiry: formatMessage({
      defaultMessage: "Card Expiration",
      description: "field label",
    }),
    cardCvv: formatMessage({
      defaultMessage: "Card CVV",
      description: "field label",
    }),
  };

  const inputFieldKeys = Object.keys(inputFields);
  const oneOfFieldsError = errors[inputFieldKeys.find((i) => errors[i])];
  const oneOfFieldsTouched = inputFieldKeys.find((i) => formState[i] && formState[i].isTouched);

  return (
    <RebillyCardResponsiveHandler>
      {Object.keys(inputFields).map((fieldName) => (
        <TextField
          key={`rebilly-field-${fieldName}`}
          name={fieldName}
          label={inputFields[fieldName]}
          hasFocus={formState[fieldName] && formState[fieldName].hasFocus}
          isTouched={formState[fieldName] && formState[fieldName].isTouched}
          error={errors[fieldName]}
          {...rest}
        />
      ))}

      {/* If one of the three input fields for Rebilly has been touched
      and has an error, and the viewport width is big enough to show all
      three components in one row, we show the first error here */}
      <FormControl isInvalid={oneOfFieldsTouched && oneOfFieldsError}>
        <FormErrorMessage className="multi-field-error-message">
          {oneOfFieldsError}
        </FormErrorMessage>
      </FormControl>
    </RebillyCardResponsiveHandler>
  );
}
